import React, { useState, useMemo, useEffect, useCallback, useContext } from 'react';
import { Layout, Menu, Typography, Button, Dropdown, Modal, Tag } from 'antd';
import { useLocation, Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RightCircleOutlined,
  LeftCircleOutlined,
  StarOutlined,
  DeleteOutlined,
  HomeOutlined,
  FileTextOutlined,
  HistoryOutlined,
  MessageOutlined,
  MenuUnfoldOutlined,
  BookOutlined,
  FolderOutlined,
  ToolOutlined,
  TranslationOutlined,
  ThunderboltOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import useQueryParams from '../../hooks/useQueryParams';
import { useChatHistory } from '../../contexts/chatHistoryContext';
import { useKnowledgeBase } from '../../contexts/knowledgeBaseContext';
import { useProjectRoutes } from '../../contexts/projectRoutesContext';
import { useDocumentParser } from '../../hooks/useDocumentParser';
import { AI_ASSISTANT_TYPES } from '../../api/aiTools';
import { batchGetMyPermissions } from '../../api/auth';
import LoadingAnimation from '../LoadingAnimation';
import AuthContext from '../../contexts/authProvider';
import { IS_STAGING } from '../../utils/config';
import './sidebar.scss'; // 修改为SCSS文件

// Role colors for display
const ROLE_COLORS = {
  owner: 'gold',
  admin: 'purple',
  editor: 'blue',
  viewer: 'default',
  system_admin: 'red',
};

const { Link } = Typography;
const { Sider } = Layout;

function getItem(label, key, icon, children, disabled, type) {
  return {
    key,
    icon,
    children,
    label,
    disabled,
    type,
  };
}

const Sidebar = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  // 定義檔案管理與工具箱選單項目
  const fileManagerMenuItems = useMemo(() => [
    getItem(<Link href="/filemanager">{t('common.title')}</Link>, 'filemanager', <HomeOutlined />, null),
    getItem(
      <Link href="/filemanager?conditions[0][field]=starred&conditions[0][operation]=boolean&conditions[0][value]=true&conditions[1][field]=trashed&conditions[1][operation]=boolean&conditions[1][value]=false">
        {t('starred')}
      </Link>,
      'starred',
      <StarOutlined />, 
      null
    ),
    getItem(
      <Link href="/filemanager?conditions[0][field]=trashed&conditions[0][operation]=boolean&conditions[0][value]=true">
        {t('trashed')}
      </Link>,
      'trashed',
      <DeleteOutlined />, 
      null
    ),
  ], [t]);
  const usefulToolsMenuItems = useMemo(() => {
    const userTier = user?.user_tier || 'user';
    const canSeeSolar = userTier === 'admin' || userTier === 'developer';

    return [
      getItem(
        t('ai-tools'),
        'ai-tools',
        <ToolOutlined />,
        [
          getItem(
            <RouterLink to={`/usefulTools/${AI_ASSISTANT_TYPES.MEETING_SUMMARY}`}>{t('meeting-summary')}</RouterLink>,
            AI_ASSISTANT_TYPES.MEETING_SUMMARY,
            <FileTextOutlined />,
            null
          ),
          getItem(
            <RouterLink to={`/usefulTools/${AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION}`}>{t('document-translation')}</RouterLink>,
            AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION,
            <TranslationOutlined />,
            null
          ),
          ...(canSeeSolar ? [
            getItem(
              <RouterLink to={`/usefulTools/${AI_ASSISTANT_TYPES.SOLAR_CALCULATION}`}>{t('solar-calculation')}</RouterLink>,
              AI_ASSISTANT_TYPES.SOLAR_CALCULATION,
              <ThunderboltOutlined />,
              null
            ),
          ] : []),
        ],
        false,
        'group'
      ),
    ];
  }, [t, user]);
  const [collapsed, setCollapsed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const queryParams = useQueryParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [openKeys, setOpenKeys] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  
  const isAskQuestion = location.pathname.startsWith('/askquestion');
  const isProject = location.pathname.startsWith('/project');
  const isDocumentParser = location.pathname.startsWith('/documentParser');
  const homePage = location.pathname.startsWith('/selectmenu');
  const { chatHistory, loadMore, hasMore, fetchChatHistory, selectedChatId, setSelectedChatId, deleteChat } = useChatHistory();
  const { 
    knowledgeBases, 
    fetchKnowledgeBases, 
    hasMore: knowledgeBaseHasMore,  // 直接從 context 中解構 hasMore
    loadMore: loadMoreKnowledgeBase // 直接從 context 中解構 loadMore
  } = useKnowledgeBase();
  const {
    projectRoutes: dynamicProjectRoutes,
    loading: loadingProjectRoutes, // Already getting loading state
    error: projectRoutesError,    // Get error state
    loadProjectRoutes           // Get the loading function
  } = useProjectRoutes();
  const { 
    projects, 
    fetchProjects, 
    loading: loadingDocumentParser 
  } = useDocumentParser();
  const [loadingStates, setLoadingStates] = useState({
    chat: false,
    loadMore: false,
    knowledgeBase: false,
    project: false,
    documentParser: false
  });
  // Store user's roles for resources
  const [projectRoles, setProjectRoles] = useState({});
  const [knowledgeBaseRoles, setKnowledgeBaseRoles] = useState({});

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 767) {
        setIsMobile(true);
        setHidden(true);
      } else {
        setIsMobile(false);
        setHidden(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const setLoading = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (location.pathname.startsWith('/knowledgeBaseManager') && knowledgeBases.length === 0) {
      setLoading('knowledgeBase', true);
      fetchKnowledgeBases()
        .finally(() => {
          setLoading('knowledgeBase', false);
        });
    }
  }, [location.pathname, fetchKnowledgeBases, knowledgeBases.length]);

  useEffect(() => {
    if (isAskQuestion) {
      setLoading('chat', true);
      fetchChatHistory()
        .finally(() => {
          setLoading('chat', false);
        });
    }
  }, [isAskQuestion, fetchChatHistory]);

  // Load projects when accessing DocumentParser sub-pages directly (e.g., monitor page)
  // but not when accessing the main DocumentParser page or schemas page which load their own projects
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const isDocumentParserSubPage = isDocumentParser && pathParts.length > 2 && pathParts[2]; // e.g., /documentParser/monitor/xxx
    const isSchemaPage = pathParts[2] === 'schemas'; // /documentParser/schemas
    
    // Only load for monitor pages, not for main page or schemas page
    if (isDocumentParserSubPage && !isSchemaPage && projects.length === 0 && !loadingDocumentParser && !loadingStates.documentParser) {
      setLoading('documentParser', true);
      fetchProjects(true) // Use silent mode to avoid global loading state
        .finally(() => {
          setLoading('documentParser', false);
        });
    }
  }, [isDocumentParser, location.pathname, fetchProjects, projects.length, loadingDocumentParser, loadingStates.documentParser]);

  // useEffect to load project routes when the sidebar mounts
  // or when the path indicates we are in the project section
  useEffect(() => {
    // Only load if we are in the project section
    if (isProject || homePage) {
      // console.log("Attempting to load project routes..."); // Debug log
      loadProjectRoutes(); // Call the function from context
    }
  }, [isProject, homePage, loadProjectRoutes]); // Add dependencies

  // Fetch batch permissions for Document Parser projects
  useEffect(() => {
    const fetchProjectRoles = async () => {
      if (projects.length === 0) return;

      const projectIds = projects.map(p => p.document_project_id);
      try {
        const result = await batchGetMyPermissions('dp_project', projectIds);
        if (result?.permissions) {
          setProjectRoles(result.permissions);
        }
      } catch (error) {
        console.error('Failed to fetch project roles:', error);
      }
    };

    if (isDocumentParser && projects.length > 0) {
      fetchProjectRoles();
    }
  }, [isDocumentParser, projects]);

  // Fetch batch permissions for Knowledge Bases
  useEffect(() => {
    const fetchKnowledgeBaseRoles = async () => {
      if (knowledgeBases.length === 0) return;

      // Knowledge bases use kb_id for permissions
      const kbIds = knowledgeBases.filter(kb => kb.kb_id).map(kb => kb.kb_id);
      if (kbIds.length === 0) return;

      try {
        const result = await batchGetMyPermissions('knowledge_base', kbIds);
        if (result?.permissions) {
          setKnowledgeBaseRoles(result.permissions);
        }
      } catch (error) {
        console.error('Failed to fetch knowledge base roles:', error);
      }
    };

    if (location.pathname.startsWith('/knowledgeBaseManager') && knowledgeBases.length > 0) {
      fetchKnowledgeBaseRoles();
    }
  }, [location.pathname, knowledgeBases]);

  // 滾動監聽
  useEffect(() => {
    const handleScroll = (e) => {
      const element = e.target;
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;

      if (isAskQuestion && hasMore && !loadingStates.loadMore && isNearBottom) {
        setLoading('loadMore', true);
        loadMore()
          .finally(() => {
            setLoading('loadMore', false);
          });
      } else if (
        location.pathname.startsWith('/knowledgeBaseManager') && 
        knowledgeBaseHasMore && 
        !loadingStates.knowledgeBase && 
        isNearBottom
      ) {
        setLoading('knowledgeBase', true);
        loadMoreKnowledgeBase()
          .finally(() => {
            setLoading('knowledgeBase', false);
          });
      }
    };

    const siderElement = document.querySelector('.ant-layout-sider-children');
    if (siderElement) {
      siderElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (siderElement) {
        siderElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [
    hasMore, 
    loadingStates, 
    loadMore, 
    isAskQuestion,
    location.pathname,
    knowledgeBaseHasMore,
    loadMoreKnowledgeBase
  ]);

  const handleNewChat = useCallback((e) => {
    e.preventDefault();
    navigate('/askquestion', { state: { clearState: true } });
  }, [navigate]);

  // 處理刪除聊天記錄
  const handleDeleteChat = useCallback((chatId, chatTitle, event) => {
    // 确保event是有效的，如果没有传入event，则不调用stopPropagation
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
      event.preventDefault();
    }
    
    Modal.confirm({
      title: t('confirm-delete'),
      icon: <ExclamationCircleOutlined />,
      content: t('confirm-delete-chat-message', { chatTitle }),
      okText: t('common.save'),
      cancelText: t('common.cancel'),
      centered: true,
      onOk: async () => {
        await deleteChat(chatId);
        // 若刪除的是當前聊天，導航到新聊天頁面
        if (location.pathname.includes(`/chat/${chatId}`)) {
          navigate('/askquestion', { state: { clearState: true } });
        }
      }
    });
  }, [deleteChat, location.pathname, navigate, t]);

  // 將動態路由轉換為菜單項
  const convertRoutesToMenuItems = useCallback((routes) => {
    if (!routes || !Array.isArray(routes)) return [];
    
    return routes.map(route => {
      // 轉換子項目 (第二層、第三層等)
      const children = route.children && route.children.length > 0
        ? route.children.map(child => {
            if (child.children && child.children.length > 0) {
              return getItem(
                <RouterLink
                  className='dual-role-menu-item'
                  to={child.path || `/project/management/${child.key}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {child.label}
                </RouterLink>,
                child.key,
                null,
                child.children.map(subChild =>
                  getItem(
                    <RouterLink to={subChild.path || `/project/management/${subChild.key}`}>
                      {subChild.label}
                    </RouterLink>,
                    subChild.key,
                    null,
                    null
                  )
                )
              );
            }
            return getItem(
              <RouterLink to={child.path || `/project/management/${child.key}`}>
                {child.label}
              </RouterLink>,
              child.key,
              null,
              null
            );
          })
        : null;
      
      // 父層：若有路徑則直接使用 RouterLink（含子項時，也是如此）
      const labelElement = route.path ? (
        <RouterLink
          to={route.path || `/project/${route.key}`}
          onClick={(e) => e.stopPropagation()}
        >
          {route.label}
        </RouterLink>
      ) : (
        route.label
      );
      
      return getItem(labelElement, route.key, route.icon, children);
    });
  }, []);

  const projectMenuItems = useMemo(() => {
    if (loadingProjectRoutes) {
      return [getItem(<LoadingAnimation size="small" />, 'loading-projects', null)];
    }
    if (projectRoutesError) {
       // Optionally display the error message
       // return [getItem(`載入失敗: ${projectRoutesError.message}`, 'error-projects', null)];
       return [getItem(t('project-menu-load-failed'), 'error-projects', null)];
    }
    // Pass dynamicProjectRoutes directly to the conversion function
    return convertRoutesToMenuItems(dynamicProjectRoutes);
  }, [dynamicProjectRoutes, loadingProjectRoutes, projectRoutesError, convertRoutesToMenuItems, t]);

  const askQuestionMenuItems = useMemo(() => {
    if (loadingStates.chat) {
      return [
        getItem(
          <a href="/askquestion" onClick={handleNewChat}>{t('chat.startNewChat')}</a>, 
          'allQuestions', 
          <FileTextOutlined />, 
          null
        ),
        getItem(
          <LoadingAnimation />,
          'loading',
          <HistoryOutlined />,
          null,
          true
        ),
      ];
    }

    // 獲取本地時間的今天開始時間
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(todayStart.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(todayStart.getTime() - 30 * 24 * 60 * 60 * 1000);

    const groupedChats = {
      [t('today')]: [],
      [t('recent-7-days')]: [],
      [t('recent-30-days')]: [],
      older: {}
    };

    const seenIds = new Set();

    chatHistory.forEach(chat => {
      if (seenIds.has(chat.chat_session_id)) return;
      seenIds.add(chat.chat_session_id);

      // 確保正確解析 UTC 時間並轉換為本地時區
      // 如果後端返回的是 UTC 時間但沒有 Z 標記，需要手動添加
      let chatTimestamp = chat.last_chat_timestamp;
      // 檢查是否有時區標記：Z 或者 +/-HH:MM 格式
      const hasTimezone = chatTimestamp.endsWith('Z') || 
                         /[+-]\d{2}:\d{2}$/.test(chatTimestamp) || 
                         /[+-]\d{4}$/.test(chatTimestamp);
      
      if (!hasTimezone) {
        chatTimestamp += 'Z'; // 明確標記為 UTC 時間
      }
      const chatDate = new Date(chatTimestamp);
      
      // 獲取聊天日期的本地時間日期開始時間（僅日期部分，時間設為 00:00:00）
      const chatDateStart = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());
      
      // 创建更多菜单项数组
      const moreMenuItems = [
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: t('delete-chat'),
          onClick: () => handleDeleteChat(chat.chat_session_id, chat.chat_title),
        }
      ];
      
      // 修改聊天项目，添加更多菜单
      const chatItem = getItem(
        <div 
          className={`chat-list-item ${openDropdownId === chat.chat_session_id ? 'dropdown-open' : ''}`}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
        >
          <RouterLink 
            to={`/askquestion/chat/${chat.chat_session_id}`}
          >
            {chat.chat_title}
          </RouterLink>
          <Dropdown 
            menu={{ items: moreMenuItems }} // 使用 menu 替代 overlay
            trigger={['click']}
            onOpenChange={(open) => setOpenDropdownId(open ? chat.chat_session_id : null)}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Button
              type="text"
              size="small"
              className="more-button"
              icon={<MoreOutlined />}
              style={{ 
                marginLeft: '5px', 
                padding: '0 5px',
                minWidth: '30px',
                height: '24px'
              }} 
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            />
          </Dropdown>
        </div>,
        `chat-${chat.chat_session_id}`,
        <MessageOutlined />,
        null
      );

      // 使用日期開始時間進行比較，避免時區問題
      const isToday = chatDateStart.getTime() === todayStart.getTime();

      if (isToday) {
        groupedChats[t('today')].push(chatItem);
      } else if (chatDateStart >= sevenDaysAgo) {
        groupedChats[t('recent-7-days')].push(chatItem);
      } else if (chatDateStart >= thirtyDaysAgo) {
        groupedChats[t('recent-30-days')].push(chatItem);
      } else {
        const yearMonth = `${chatDate.getFullYear()}/${String(chatDate.getMonth() + 1).padStart(2, '0')}`;
        if (!groupedChats.older[yearMonth]) {
          groupedChats.older[yearMonth] = [];
        }
        groupedChats.older[yearMonth].push(chatItem);
      }
    });

    const menuItems = [
      getItem(
        <a href="/askquestion" onClick={handleNewChat}>{t('chat.startNewChat')}</a>, 
        'allQuestions', 
        <FileTextOutlined />, 
        null
      ),
    ];

    if (groupedChats[t('today')].length > 0) {
      menuItems.push(getItem(t('today'), 'today', null, groupedChats[t('today')], false, 'group'));
    }

    // Add recent chats
    if (groupedChats[t('recent-7-days')].length > 0) {
      menuItems.push(getItem(t('recent-7-days'), 'recent7', null, groupedChats[t('recent-7-days')], false, 'group'));
    }
    
    if (groupedChats[t('recent-30-days')].length > 0) {
      menuItems.push(getItem(t('recent-30-days'), 'recent30', null, groupedChats[t('recent-30-days')], false, 'group'));
    }

    // Add older chats grouped by year/month
    Object.entries(groupedChats.older)
      .sort((a, b) => b[0].localeCompare(a[0])) // Sort by date descending
      .forEach(([yearMonth, chats]) => {
        menuItems.push(getItem(yearMonth, `month-${yearMonth}`, null, chats, false, 'group'));
      });

    // 移除原本的"顯示更多"按鈕，改為在底部顯示載入中動畫
    if (hasMore && loadingStates.loadMore) {
      menuItems.push(
        getItem(
          <LoadingAnimation />,
          'loading-more',
          null,
          null,
          true
        )
      );
    }

    return menuItems;
  }, [chatHistory, hasMore, loadingStates, handleNewChat, handleDeleteChat, openDropdownId, t]);

  const knowledgeBaseManagerMenuItems = useMemo(() => {
    if (loadingStates.knowledgeBase) {
      return [
        getItem(<Link href="/knowledgeBaseManager">{t('common.menu.knowledgeBaseManager')}</Link>, 'knowledgeBaseManager', <BookOutlined />, null),
        getItem(
          t('knowledge-bases-created'),
          'knowledgeBaseList',
          <BookOutlined />,
          [getItem(<LoadingAnimation />, 'loading-kb', null, null, true)],
          false,
          'group'
        ),
      ];
    }

    const subMenuItems = knowledgeBases.sort((a, b) => a.knowledge_base_name.localeCompare(b.knowledge_base_name)).map(kb => {
      const roleInfo = kb.kb_id ? knowledgeBaseRoles[kb.kb_id] : null;
      const isSystemAdmin = roleInfo?.isSystemAdmin;
      const resourceRole = roleInfo?.roleId;

      return getItem(
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
          <RouterLink to={`/knowledgeBaseManager/${kb.kb_id}`} style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {kb.knowledge_base_name}
          </RouterLink>
          <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
            {isSystemAdmin && (
              <Tag color={ROLE_COLORS['system_admin'] || 'red'} style={{ marginRight: 0, fontSize: '10px', lineHeight: '16px', padding: '0 4px' }}>
                {t('permission.roles.system_admin')}
              </Tag>
            )}
            {resourceRole && (
              <Tag color={ROLE_COLORS[resourceRole] || 'default'} style={{ marginRight: 0, fontSize: '10px', lineHeight: '16px', padding: '0 4px' }}>
                {t(`permission.roles.${resourceRole}`)}
              </Tag>
            )}
          </div>
        </div>,
        `kb-${kb.kb_id}`,
        <FolderOutlined />,
        null
      );
    });

    // 添加載入中動畫
    if (knowledgeBaseHasMore && loadingStates.knowledgeBase) {
      subMenuItems.push(
        getItem(
          <LoadingAnimation />,
          'loading-kb-more',
          null,
          null,
          true
        )
      );
    }

    return [
      getItem(<Link href="/knowledgeBaseManager">{t('common.menu.knowledgeBaseManager')}</Link>, 'knowledgeBaseManager', <BookOutlined />, null),
      getItem(
        t('knowledge-bases-created'),
        'knowledgeBaseList',
        <BookOutlined />,
        subMenuItems,
        false,
        'group'
      ),
    ];
  }, [knowledgeBases, loadingStates, knowledgeBaseHasMore, knowledgeBaseRoles, t]);

  const documentParserMenuItems = useMemo(() => {
    // Only show loading state when initially loading projects, not during other operations
    if (loadingStates.documentParser && projects.length === 0) {
      return [
        getItem(<Link href="/documentParser">{t('documentParser.menu')}</Link>, 'documentParser', <FileTextOutlined />, null),
        getItem(
          <LoadingAnimation />,
          'loading-projects',
          null,
          null,
          true
        ),
      ];
    }

    // 去重處理，避免重複的專案項目
    const uniqueProjects = projects.filter((project, index, self) => 
      index === self.findIndex(p => p.document_project_id === project.document_project_id)
    );

    const projectMenuItems = uniqueProjects.map(project => {
      const roleInfo = projectRoles[project.document_project_id];
      const isSystemAdmin = roleInfo?.isSystemAdmin;
      const resourceRole = roleInfo?.roleId;

      return getItem(
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
          <RouterLink to={`/documentParser/monitor/${project.document_project_id}`} style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {project.project_name}
          </RouterLink>
          <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
            {isSystemAdmin && (
              <Tag color={ROLE_COLORS['system_admin'] || 'red'} style={{ marginRight: 0, fontSize: '10px', lineHeight: '16px', padding: '0 4px' }}>
                {t('permission.roles.system_admin')}
              </Tag>
            )}
            {resourceRole && (
              <Tag color={ROLE_COLORS[resourceRole] || 'default'} style={{ marginRight: 0, fontSize: '10px', lineHeight: '16px', padding: '0 4px' }}>
                {t(`permission.roles.${resourceRole}`)}
              </Tag>
            )}
          </div>
        </div>,
        `parser-project-${project.document_project_id}`,
        <FileTextOutlined />,
        null
      );
    });

    return [
      getItem(<Link href="/documentParser">{t('documentParser.title')}</Link>, 'documentParser', <FileTextOutlined />, null),
      getItem(<RouterLink to="/documentParser/schemas">{t('documentParser.schemaManager.title')}</RouterLink>, 'documentParserSchemas', <BookOutlined />, null),
      getItem(
        t('documentParser.totalProjects'),
        'documentParserProjects',
        <FileTextOutlined />,
        projectMenuItems,
        false,
        'group'
      ),
    ];
  }, [projects, loadingStates.documentParser, projectRoles, t]);

  const handleMenuClick = ({ key }) => {
    if (key.startsWith('chat-')) {
      const chatId = key.replace('chat-', '');
      setSelectedChatId(chatId);
    }
  };

  const menuItems = useMemo(() => {
    if (location.pathname.startsWith('/askquestion')) {
      return askQuestionMenuItems;
    }
    if (location.pathname.startsWith('/knowledgeBaseManager')) {
      return knowledgeBaseManagerMenuItems;
    }
    if (location.pathname.startsWith('/usefulTools')) {
      return usefulToolsMenuItems;
    }
    if (location.pathname.startsWith('/documentParser')) {
      return documentParserMenuItems;
    }
    if (location.pathname.startsWith('/project')) {
      return projectMenuItems;
    }

    // Hide FileManager menu in STAGING environment
    if (location.pathname.startsWith('/filemanager') && !IS_STAGING) {
      return fileManagerMenuItems;
    }
  }, [location.pathname, askQuestionMenuItems, knowledgeBaseManagerMenuItems, usefulToolsMenuItems, documentParserMenuItems, projectMenuItems, fileManagerMenuItems]);

  // 找出當前選中的選單項
  const selectedKeys = useMemo(() => {
    if (location.pathname.startsWith('/askquestion')) {
      if (selectedChatId) {
        return [`chat-${selectedChatId}`];
      }
      const pathParts = location.pathname.split('/');
      if (pathParts[2] === 'chat') {
        return [`chat-${pathParts[3]}`];
      }
      return ['allQuestions'];
    }
    if (location.pathname.startsWith('/knowledgeBaseManager')) {
      const pathParts = location.pathname.split('/');
      if (pathParts.length > 2 && pathParts[2] !== '') {
        // URL now contains kb_id instead of knowledge_base_name
        return [`kb-${pathParts[2]}`];
      }
      return ['knowledgeBaseManager'];
    }
    if (location.pathname.startsWith('/usefulTools')) {
      const pathParts = location.pathname.split('/');
      return [pathParts[2] || AI_ASSISTANT_TYPES.MEETING_SUMMARY, 'ai-tools'];
    }
    if (location.pathname.startsWith('/documentParser')) {
      const pathParts = location.pathname.split('/');
      if (pathParts.length > 3 && pathParts[2] === 'monitor') {
        return [`parser-project-${pathParts[3]}`];
      }
      if (pathParts.length > 3 && pathParts[2] === 'settings') {
        return [`parser-project-${pathParts[3]}`];
      }
      if (pathParts.length > 2 && pathParts[2] === 'schemas') {
        return ['documentParserSchemas'];
      }
      return ['documentParser'];
    }
    if (location.pathname.startsWith('/project')) {
      const pathParts = location.pathname.split('/');
      // 對於 /project/management/* 格式的路徑
      if (pathParts.length > 3 && pathParts[2] === 'management') {
        // 處理多層級路徑結構
        const deepestName = pathParts[pathParts.length - 1]; // 取得最深層的名稱
        const displayName = decodeURIComponent(deepestName);
        
        // 遍歷 dynamicProjectRoutes 尋找匹配的 item
        const findKeyByDisplayName = (routes) => {
          let matchingKeys = [];
          
          // 檢查所有層級是否有匹配的項目
          const checkRoute = (route, parentKeys = []) => {
            // 檢查當前項目是否匹配
            if (route.label === displayName || 
                (route.path && route.path.includes(`/${displayName}`)) ||
                (route.path && route.path === location.pathname)) {
              return [...parentKeys, route.key];
            }
            
            // 檢查子項目
            if (route.children && route.children.length > 0) {
              for (const child of route.children) {
                const result = checkRoute(child, [...parentKeys, route.key]);
                if (result.length > 0) {
                  return result;
                }
              }
            }
            
            return [];
          };
          
          // 檢查每個頂層路由
          for (const route of routes) {
            const keys = checkRoute(route);
            if (keys.length > 0) {
              matchingKeys = keys;
              break;
            }
          }
          
          // 如果以上策略沒找到，使用簡單的字符串匹配
          if (matchingKeys.length === 0) {
            // 嘗試通過 table_name 或 primaryViewId 匹配
            for (const route of routes) {
              if (route.children) {
                for (const child of route.children) {
                  // 如果 key 包含 displayName 或 顯示名稱相似
                  if (child.key.includes(displayName) || 
                      child.label === displayName || 
                      (child.label && displayName && 
                       child.label.toLowerCase().includes(displayName.toLowerCase()))) {
                    matchingKeys = [child.key];
                    break;
                  }
                  
                  if (child.children) {
                    for (const subChild of child.children) {
                      if (subChild.key.includes(displayName) || 
                          subChild.label === displayName || 
                          (subChild.label && displayName && 
                           subChild.label.toLowerCase().includes(displayName.toLowerCase()))) {
                        matchingKeys = [subChild.key];
                        break;
                      }
                    }
                  }
                }
              }
            }
          }
          
          // 檢查完整路徑匹配
          if (matchingKeys.length === 0) {
            const fullPath = location.pathname;
            const findByPath = (routes) => {
              for (const route of routes) {
                if (route.path === fullPath) {
                  return [route.key];
                }
                if (route.children) {
                  for (const child of route.children) {
                    if (child.path === fullPath) {
                      return [child.key];
                    }
                    if (child.children) {
                      for (const subChild of child.children) {
                        if (subChild.path === fullPath) {
                          return [subChild.key];
                        }
                      }
                    }
                  }
                }
              }
              return [];
            };
            matchingKeys = findByPath(dynamicProjectRoutes);
          }
          
          // 返回找到的最匹配的鍵
          return matchingKeys.length > 0 ? [matchingKeys[matchingKeys.length - 1]] : [];
        };
        
        const matchedKeys = findKeyByDisplayName(dynamicProjectRoutes);
        return matchedKeys.length > 0 ? matchedKeys : [];
      } else if (pathParts.length > 2 && pathParts[2]) {
        // 直接子路由，如 /project/dashboard
        return [pathParts[2]];
      }
      return ['dashboard']; // 預設選中儀表板
    }
    const item = queryParams?.find?.(
      (item) => item.field === 'starred' || (item.field === 'trashed' && item.value === true)
    );
    return item ? [item.field] : ['filemanager'];
  }, [queryParams, location.pathname, selectedChatId, dynamicProjectRoutes]);

  // 根據當前路徑設置默認展開的項目
  useEffect(() => {
    if (isProject && dynamicProjectRoutes.length > 0) {
      const pathParts = location.pathname.split('/');
      if (pathParts.length > 3 && pathParts[2] === 'management') {
        // 處理多層級路徑結構
        const deepestName = pathParts[pathParts.length - 1]; // 取得最深層的名稱
        const displayName = decodeURIComponent(deepestName);
        const fullPath = location.pathname; // 完整路徑
        
        // 找出應該展開的父項目 keys
        const findParentKeys = (routes) => {
          const parentKeys = new Set();
          
          // 檢查所有層級，收集父項目的 keys
          const checkAndCollectParents = (route, parents = []) => {
            // 檢查當前項目是否匹配
            const isMatch = 
              route.label === displayName || 
              (route.path && route.path.includes(`/${displayName}`)) ||
              (route.path && route.path === fullPath);
            
            if (isMatch) {
              // 添加所有父項目的 keys
              parents.forEach(key => parentKeys.add(key));
              return true;
            }
            
            // 檢查子項目
            if (route.children && route.children.length > 0) {
              for (const child of route.children) {
                // 將當前項目添加為父項
                const newParents = [...parents, route.key];
                if (checkAndCollectParents(child, newParents)) {
                  return true;
                }
              }
            }
            
            return false;
          };
          
          // 檢查每個頂層路由
          for (const route of routes) {
            if (checkAndCollectParents(route, [])) {
              break;
            }
          }
          
          // 如果以上策略沒找到匹配，使用簡單的字符串匹配
          if (parentKeys.size === 0) {
            for (const route of routes) {
              if (route.children) {
                for (const child of route.children) {
                  if (child.key.includes(displayName) || 
                      child.label === displayName ||
                      (child.label && displayName && 
                       child.label.toLowerCase().includes(displayName.toLowerCase()))) {
                    parentKeys.add(route.key);
                    break;
                  }
                  
                  if (child.children) {
                    for (const subChild of child.children) {
                      if (subChild.key.includes(displayName) || 
                          subChild.label === displayName ||
                          (subChild.label && displayName && 
                           subChild.label.toLowerCase().includes(displayName.toLowerCase()))) {
                        parentKeys.add(route.key);
                        parentKeys.add(child.key);
                        break;
                      }
                    }
                  }
                }
              }
            }
          }
          
          // 檢查完整路徑匹配
          if (parentKeys.size === 0) {
            const findParentsByPath = (routes, parents = []) => {
              for (const route of routes) {
                const currentParents = [...parents, route.key];
                if (route.path === fullPath) {
                  // 返回所有父項目的 keys
                  return parents;
                }
                if (route.children) {
                  for (const child of route.children) {
                    const childParents = [...currentParents];
                    if (child.path === fullPath) {
                      return childParents;
                    }
                    if (child.children) {
                      for (const subChild of child.children) {
                        if (subChild.path === fullPath) {
                          return [...childParents, child.key];
                        }
                      }
                    }
                  }
                }
              }
              return [];
            };
            
            const pathParentKeys = findParentsByPath(dynamicProjectRoutes);
            pathParentKeys.forEach(key => parentKeys.add(key));
          }
          
          return Array.from(parentKeys);
        };
        
        const parentKeys = findParentKeys(dynamicProjectRoutes);
        if (parentKeys.length > 0) {
          setOpenKeys(prevKeys => {
            // 保留已手動展開的項目，並添加新的項目
            const newKeys = new Set([...prevKeys, ...parentKeys]);
            return Array.from(newKeys);
          });
        }
      }
    }
  }, [isProject, dynamicProjectRoutes, location.pathname]);

  const handleMenuOpenChange = (keys) => {
    // 當菜單被手動展開或收起時，保存最新的狀態
    setOpenKeys(keys);
  };

  // 確保在菜單項切換時，清除 openKeys 狀態
  useEffect(() => {
    // 監測 location.pathname 的變化，當切換到不同頁面時重置 openKeys
    // 但是排除 /project 下的頁面跳轉，因為我們希望保持項目菜單的展開狀態
    const currentSection = location.pathname.split('/')[1]; // 獲取第一段路徑
    
    // 如果切換到了不同的主要部分（例如從 /project 切換到 /askquestion）
    if (currentSection && currentSection !== 'project' && openKeys.length > 0) {
      // 只在非項目頁面間切換時重置展開項
      setOpenKeys([]);
    }
  }, [location.pathname, openKeys]);

  const toggleSidebar = () => {
    if (isMobile) {
      setHidden(!hidden);
    }
  };

  return (
    <>
      {isMobile && hidden && (
        <Button
          className="sidebar-toggle"
          onClick={toggleSidebar}
          icon={<MenuUnfoldOutlined />}
        />
      )}
      <Sider
        collapsible
        className={`app-aside ${isMobile && hidden ? 'hidden' : ''}`}
        collapsed={collapsed}
        collapsedWidth={60}
        onCollapse={(value) => setCollapsed(value)}
        trigger={collapsed ? <RightCircleOutlined /> : <LeftCircleOutlined />}
        width={250}
      >
        <Menu
          mode="inline"
          className="app-aside-menu chat-history-menu"
          inlineIndent={12}
          selectedKeys={selectedKeys}
          openKeys={!collapsed ? openKeys : []}
          onOpenChange={handleMenuOpenChange}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      {isMobile && !hidden && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}
    </>
  );
};

export default Sidebar;
